// Account form validation — FE-040.
//
// Pure functions: no side effects, no React. The page calls
// ``validateAccountForm`` before invoking the mutation; tests can
// exercise the rules directly without mounting the form.

import type {
  AccountFormValues,
  CreateAccountInput,
} from "./types";


/** Format an EIN-shaped string as ``XX-XXXXXXX``.
 *
 * Accepts anything the user has typed so far — partial digits, with
 * or without the dash, with leading/trailing whitespace — and emits
 * the canonical IRS shape (2 digits, dash, up to 7 digits). Empty
 * input round-trips as empty so the form's "no EIN" state is
 * preserved.
 *
 * Examples:
 *   ``""``         → ``""``
 *   ``"12"``       → ``"12"``  (still typing the prefix)
 *   ``"123"``      → ``"12-3"``
 *   ``"123456789"``→ ``"12-3456789"``
 *   ``"12-3456789012"`` → ``"12-3456789"`` (truncated to 9 digits)
 */
export function formatEin(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}


const _EIN_RE = /^\d{2}-\d{7}$/;


/** True if the value is either empty or fully formed (XX-XXXXXXX). */
export function isCompleteEin(raw: string): boolean {
  if (!raw || raw.trim() === "") return true;
  return _EIN_RE.test(raw.trim());
}


export type AccountFormErrors = Partial<
  Record<keyof AccountFormValues, string>
>;


export function blankFormValues(): AccountFormValues {
  return {
    legal_name: "",
    dba_name: "",
    ein: "",
    industry: "",
    employee_count_total: "",
    employee_count_eligible: "",
    bor_signed_at: "",
    primary_address_line1: "",
    primary_address_line2: "",
    primary_address_city: "",
    primary_address_state: "",
    primary_address_zip: "",
    notes: "",
    primary_location_id: "",
    naics_sector: "",
    naics_code: "",
    naics_title: "",
    risk_class: "",
    account_tag_ids: [],
  };
}


export function validateAccountForm(
  values: AccountFormValues,
): { errors: AccountFormErrors; ok: boolean } {
  const errors: AccountFormErrors = {};
  if (values.legal_name.trim().length === 0) {
    errors.legal_name = "Legal name is required.";
  }
  if (values.naics_sector.trim().length === 0) {
    errors.naics_sector = "Industry sector is required.";
  }
  if (values.ein.trim() !== "" && !isCompleteEin(values.ein)) {
    errors.ein = "EIN must be 9 digits (XX-XXXXXXX).";
  }
  const total = parseEmployeeCount(values.employee_count_total);
  const eligible = parseEmployeeCount(values.employee_count_eligible);
  if (total === "invalid") {
    errors.employee_count_total =
      "Total employees must be a non-negative whole number.";
  }
  if (eligible === "invalid") {
    errors.employee_count_eligible =
      "Eligible employees must be a non-negative whole number.";
  }
  if (
    typeof total === "number" &&
    typeof eligible === "number" &&
    eligible > total
  ) {
    errors.employee_count_eligible =
      "Eligible employees cannot exceed total employees.";
  }
  return { errors, ok: Object.keys(errors).length === 0 };
}


type ParsedCount = number | null | "invalid";


function parseEmployeeCount(raw: string): ParsedCount {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return "invalid";
  if (!Number.isInteger(n)) return "invalid";
  if (n < 0) return "invalid";
  return n;
}


export function serializeFormValues(
  values: AccountFormValues,
): CreateAccountInput {
  const totalParsed = parseEmployeeCount(values.employee_count_total);
  const eligibleParsed = parseEmployeeCount(values.employee_count_eligible);
  return {
    legal_name: values.legal_name.trim(),
    dba_name: emptyToNull(values.dba_name),
    ein: emptyToNull(values.ein),
    industry: emptyToNull(values.industry),
    employee_count_total:
      typeof totalParsed === "number" ? totalParsed : null,
    employee_count_eligible:
      typeof eligibleParsed === "number" ? eligibleParsed : null,
    bor_signed_at: emptyToNull(values.bor_signed_at),
    primary_address_line1: emptyToNull(values.primary_address_line1),
    primary_address_line2: emptyToNull(values.primary_address_line2),
    primary_address_city: emptyToNull(values.primary_address_city),
    primary_address_state: emptyToNull(values.primary_address_state),
    primary_address_zip: emptyToNull(values.primary_address_zip),
    notes: emptyToNull(values.notes),
    primary_location_id: emptyToNull(values.primary_location_id),
    naics_sector: emptyToNull(values.naics_sector),
    naics_code: emptyToNull(values.naics_code),
    risk_class:
      values.risk_class === "" ? null : values.risk_class,
    account_tag_ids: values.account_tag_ids,
  };
}


function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
